import type { Permission } from '@homecare/contracts';
import {
  BadgeCheck,
  Banknote,
  CalendarCheck,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  FileText,
  HeartHandshake,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Stamp,
  Tags,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * The duty board (§12).
 *
 * Four sections, in the order the day runs through them: Care · Operations ·
 * Revenue · Admin. Each item names the permission that governs it, and the
 * permission strings come from the catalogue in packages/contracts — a typo
 * here is a type error rather than an item that silently never appears.
 *
 * ## Hidden, never disabled
 *
 * §12: "Empty sections are hidden, not disabled." A greyed-out **Revenue**
 * section tells a scheduler that billing exists, that someone at their agency
 * can reach it, and that they are not that person. That is an organisational
 * fact they were not given, inferred from a UI affordance — a permission leak
 * with no request behind it and nothing in the audit log.
 *
 * So an item the caller cannot use is not rendered, and a section whose items
 * all disappear takes the section label and its rule with it.
 *
 * None of this is access control. `navigationFor` decides what to *draw*; the
 * route guard decides what can be *reached*, and the API decides what can be
 * *read*. A person who edits the permission list in devtools gets a longer menu
 * and the same 403s.
 */
export interface NavItem {
  label: string;
  to: string;
  /**
   * The item's mark, from Lucide (§05's base set).
   *
   * Required, not optional. An icon beside every item and none beside one is
   * worse than none at all — the odd one out reads as a different kind of
   * thing. Making it part of the type means an item cannot reach the rail
   * without someone deciding what it looks like.
   */
  icon: LucideIcon;
  /** The permission that governs it. Undefined means everyone signed in. */
  permission?: Permission;
}

export interface NavSection {
  label: string;
  /**
   * Where the section is reached from.
   *
   * `rail` is daily work. `account` is administration — People, Roles,
   * Settings, Audit log — which lives in the user menu, where settings live in
   * every other product a person uses.
   *
   * This is a placement decision, not an access one. Both kinds are filtered by
   * the same permissions, routed by the same guard, and described by the same
   * copy table; `NAVIGATION` stays the one map so none of that can drift.
   */
  placement?: 'rail' | 'account';
  items: NavItem[];
}

/**
 * The most rows the rail may carry, for someone holding every permission.
 *
 * ## Measured, not estimated
 *
 * The previous version of this comment said "roughly 550px, which clears the
 * header on a 768px laptop with room left". That was an estimate, it was wrong
 * by about 100px, and an estimate is exactly how a budget stops protecting
 * anything. These numbers come from `getBoundingClientRect` in a real Chromium
 * at the density that ships:
 *
 *     brand block          64px   (mark, product name, agency)
 *     nav item             36px   + 2px gap = 38px pitch
 *     section label        18px
 *     11 items, 3 labels  502px   total scrolling content
 *
 * The rail is full height and the brand block is the only fixed thing above the
 * list, so the scrolling region is `viewport − 64`. Eleven items therefore fit
 * down to a **566px viewport**, measured: at 566 the content is exactly the
 * client height, and at 560 it overflows by 6px.
 *
 * That is 61px better than it was, because the user block moved out of the rail
 * and into the header — its menu opened upward over the navigation, which is
 * the one thing a navigation menu must not cover.
 *
 * Below 566 it scrolls, and the scrollbar is styled for it (`.scroll-rail` in
 * `packages/ui/src/styles.css`): thin, no arrows, no track, a translucent thumb
 * that appears on hover or focus. Overflow is never hidden. A rail someone
 * cannot scroll is worse than one they can.
 *
 * ## Why this is 11 and not 16
 *
 * At a full 768px *viewport* the measured fit is 16 items — 202px of headroom,
 * five more rows. Setting the constant there would make it true and useless: a
 * budget that permits five items nobody has room for is not protecting
 * anything. The useful figure is the working height, and at a 600px viewport
 * the headroom is 34px — less than one row.
 *
 * So it is held at 11 — the number the rail carries today, with real room at
 * the height people work at. It cannot go lower without removing an item, which
 * is a product decision and not a layout one.
 *
 * `navigation.spec.ts` asserts it. Adding a twelfth item to the rail is a red
 * build and a conversation, not a quiet regression someone notices in a demo.
 * If a twelfth is genuinely needed, the lever is moving one into the user menu,
 * the way Admin went.
 */
export const RAIL_ITEM_BUDGET = 11;

export const NAVIGATION: NavSection[] = [
  {
    label: 'Care',
    items: [
      { label: 'Clients', to: '/clients', icon: Users, permission: 'client:read' },
      { label: 'Care plans', to: '/care-plans', icon: ClipboardList, permission: 'careplan:read' },
      {
        label: 'Assessments',
        to: '/assessments',
        icon: ClipboardCheck,
        permission: 'assessment:perform',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Schedule', to: '/schedule', icon: CalendarDays, permission: 'schedule:read' },
      { label: 'Visits', to: '/visits', icon: CalendarCheck, permission: 'visit:read' },
      {
        label: 'Caregivers',
        to: '/caregivers',
        icon: HeartHandshake,
        permission: 'caregiver:read',
      },
      { label: 'Credentials', to: '/credentials', icon: BadgeCheck, permission: 'credential:read' },
    ],
  },
  {
    label: 'Revenue',
    items: [
      { label: 'Billing', to: '/billing', icon: Receipt, permission: 'billing:read' },
      { label: 'Invoices', to: '/invoices', icon: FileText, permission: 'invoice:read' },
      { label: 'Payroll', to: '/payroll', icon: Banknote, permission: 'payroll:read' },
      { label: 'Rates', to: '/rates', icon: Tags, permission: 'rate:read' },
    ],
  },
  {
    label: 'Admin',
    // Administration, not daily work. A scheduler opens the rail forty times a
    // day and never opens any of these.
    placement: 'account',
    items: [
      { label: 'People', to: '/admin/people', icon: UserCog, permission: 'admin:users' },
      { label: 'Roles', to: '/admin/roles', icon: ShieldCheck, permission: 'admin:roles' },
      { label: 'Settings', to: '/admin/settings', icon: Settings, permission: 'admin:settings' },
      {
        // `credential:read` rather than an admin permission: §10.4 gives HR the
        // credential permissions and not `admin:settings`, and HR is who
        // maintains this list. Gating it on administration would put the page
        // out of reach of the only role that needs it.
        label: 'Credential types',
        to: '/admin/credential-types',
        icon: Stamp,
        permission: 'credential:read',
      },
      { label: 'Audit log', to: '/admin/audit', icon: ScrollText, permission: 'admin:audit_log' },
    ],
  },
];

function visibleSections(
  permissions: readonly string[],
  placement: NavSection['placement'],
): NavSection[] {
  const held = new Set(permissions);

  return NAVIGATION.filter((section) => (section.placement ?? 'rail') === placement)
    .map((section) => ({
      label: section.label,
      items: section.items.filter((item) => !item.permission || held.has(item.permission)),
    }))
    .filter((section) => section.items.length > 0);
}

/** The rail's sections and items. Empty sections drop out. */
export function navigationFor(permissions: readonly string[]): NavSection[] {
  return visibleSections(permissions, 'rail');
}

/** The administration items, for the user menu. Flat — there is one section. */
export function accountItemsFor(permissions: readonly string[]): NavItem[] {
  return visibleSections(permissions, 'account').flatMap((section) => section.items);
}

/**
 * Everything the caller can reach, for the command palette.
 *
 * The palette searches both placements. A palette that cannot find Settings
 * because Settings moved into a menu is a palette that has stopped being the
 * fast path — and there is no disclosure in it, because both halves are
 * filtered by the same permissions.
 */
export function searchableFor(
  permissions: readonly string[],
): { label: string; to: string; section: string }[] {
  return [...visibleSections(permissions, 'rail'), ...visibleSections(permissions, 'account')]
    .flatMap((section) => section.items.map((item) => ({ ...item, section: section.label })))
    .map(({ label, to, section }) => ({ label, to, section }));
}

/**
 * What the header calls the screen you are on.
 *
 * The reference names the section in the header bar and lets the page below it
 * say whatever it wants — "Overview" above "Good day", "Schedule" above
 * "Schedule". Read from `NAVIGATION` rather than a second table, so a renamed
 * item is renamed in both places or in neither.
 *
 * Not permission-filtered, and it does not need to be: reaching a path you
 * cannot open is already answered by the route guard, and this only ever
 * describes a path the reader is currently on.
 */
export function labelForPath(path: string): string | undefined {
  if (path === '/') return 'Overview';

  for (const section of NAVIGATION) {
    for (const item of section.items) {
      if (path === item.to || path.startsWith(`${item.to}/`)) return item.label;
    }
  }

  return undefined;
}

/** The permission governing a path, for the route guard. */
export function permissionForPath(path: string): Permission | undefined {
  for (const section of NAVIGATION) {
    for (const item of section.items) {
      if (path === item.to || path.startsWith(`${item.to}/`)) return item.permission;
    }
  }
  return undefined;
}
